import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { PolicyVerdict, RuntimeDecision, TraceStepStatus } from '@shared/trace';

// ------------------------------------------------------------------- layout

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`mb-card ${className}`}>{children}</section>;
}

export function CardHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-card__header">
      <div className="mb-grow">
        <h2 className="mb-title" style={{ fontSize: 'var(--fs-lg)' }}>
          {title}
        </h2>
        {subtitle ? <p className="mb-subtitle" style={{ marginTop: 2 }}>{subtitle}</p> : null}
      </div>
      {actions ? <div className="mb-row">{actions}</div> : null}
    </header>
  );
}

export function CardBody({ children, flush = false }: { children: ReactNode; flush?: boolean }) {
  return <div className={`mb-card__body${flush ? ' mb-card__body--flush' : ''}`}>{children}</div>;
}

// ------------------------------------------------------------------ buttons

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'subtle';
  size?: 'md' | 'sm';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  ariaLabel?: string;
  ariaPressed?: boolean;
};

export function Button({
  children,
  onClick,
  variant = 'default',
  size = 'md',
  disabled,
  type = 'button',
  title,
  ariaLabel,
  ariaPressed,
}: ButtonProps) {
  const classes = ['mb-btn'];
  if (variant === 'primary') classes.push('mb-btn--primary');
  if (variant === 'subtle') classes.push('mb-btn--subtle');
  if (size === 'sm') classes.push('mb-btn--sm');
  return (
    <button
      type={type}
      className={classes.join(' ')}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------- badges

type BadgeTone = 'neutral' | 'allow' | 'approval' | 'block' | 'info' | 'monitor';

/**
 * Severity and verdict are always carried by a glyph and a word in addition to
 * colour. Colour alone would fail anyone reading this in greyscale or with a
 * colour vision deficiency — and in a security product that is not a detail.
 */
const TONE_GLYPH: Record<BadgeTone, string> = {
  neutral: '\u25CB',
  allow: '\u2713',
  approval: '\u23F8',
  block: '\u2717',
  info: '\u2139',
  monitor: '\u25CE',
};

export function Badge({
  tone = 'neutral',
  children,
  glyph = true,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  glyph?: boolean;
}) {
  return (
    <span className={`mb-badge mb-badge--${tone}`}>
      {glyph ? (
        <span className="mb-badge__glyph" aria-hidden="true">
          {TONE_GLYPH[tone]}
        </span>
      ) : null}
      {children}
    </span>
  );
}

const VERDICT_TONE: Record<PolicyVerdict, BadgeTone> = {
  allow: 'allow',
  block: 'block',
  require_approval: 'approval',
  log: 'monitor',
};

const VERDICT_LABEL: Record<PolicyVerdict, string> = {
  allow: 'Allowed',
  block: 'Blocked',
  require_approval: 'Approval required',
  log: 'Logged',
};

export function VerdictBadge({ verdict }: { verdict: PolicyVerdict }) {
  return <Badge tone={VERDICT_TONE[verdict]}>{VERDICT_LABEL[verdict]}</Badge>;
}

const DECISION_TONE: Record<RuntimeDecision, BadgeTone> = {
  allow: 'allow',
  block: 'block',
  approval: 'approval',
  mask: 'info',
};

const DECISION_LABEL: Record<RuntimeDecision, string> = {
  allow: 'Allowed',
  block: 'Blocked',
  approval: 'Approval required',
  mask: 'Redacted',
};

export function DecisionBadge({ decision }: { decision: RuntimeDecision }) {
  return <Badge tone={DECISION_TONE[decision]}>{DECISION_LABEL[decision]}</Badge>;
}

export function RiskBadge({ level }: { level: 'low' | 'medium' | 'high' | 'critical' }) {
  const tone: BadgeTone = level === 'critical' || level === 'high' ? 'block' : level === 'medium' ? 'approval' : 'neutral';
  return (
    <Badge tone={tone} glyph={false}>
      {level[0].toUpperCase() + level.slice(1)}
    </Badge>
  );
}

// -------------------------------------------------------------- status dots

export function StatusDot({
  status,
  label,
}: {
  status: 'online' | 'stale' | 'offline';
  label?: string;
}) {
  const spoken = label ?? status;
  return (
    <>
      <span className={`mb-dot mb-dot--${status}`} aria-hidden="true" />
      <span className="mb-sr">{spoken}</span>
    </>
  );
}

export const STEP_STATUS_LABEL: Record<TraceStepStatus, string> = {
  pending: 'Not started',
  running: 'In progress',
  waiting: 'Waiting',
  done: 'Complete',
  skipped: 'Skipped',
  failed: 'Failed',
};

// -------------------------------------------------------------------- forms

export function Field({
  label,
  htmlFor,
  help,
  children,
}: {
  label: string;
  htmlFor: string;
  help?: string;
  children: ReactNode;
}) {
  const helpId = help ? `${htmlFor}-help` : undefined;
  return (
    <div className="mb-field">
      <label className="mb-field__label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {help ? (
        <span className="mb-field__help" id={helpId}>
          {help}
        </span>
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------------- tables

export interface Column<T> {
  key: string;
  header: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
  width?: string;
}

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  caption,
  empty = 'Nothing to show.',
}: {
  columns: Array<Column<T>>;
  rows: T[];
  getRowKey: (row: T) => string;
  caption: string;
  empty?: string;
}) {
  if (rows.length === 0) {
    return <p className="mb-empty">{empty}</p>;
  }
  return (
    <table className="mb-table">
      <caption className="mb-sr">{caption}</caption>
      <thead>
        <tr>
          {columns.map(column => (
            <th key={column.key} scope="col" className={column.align === 'right' ? 'mb-right' : undefined} style={column.width ? { width: column.width } : undefined}>
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={getRowKey(row)}>
            {columns.map(column => (
              <td key={column.key} className={column.align === 'right' ? 'mb-right' : undefined}>
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="mb-empty">{children}</p>;
}

// ---------------------------------------------------------- presentation mode

const PRESENTATION_KEY = 'meridian.presentation';

/**
 * Presentation mode enlarges type and loosens spacing for a projector or a
 * screen share. It deliberately hides nothing — a demo that shows the audience
 * less than the driver sees is a demo that invites suspicion.
 */
export function usePresentationMode(): [boolean, () => void] {
  const [enabled, setEnabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(PRESENTATION_KEY) === 'true';
  });

  useEffect(() => {
    document.documentElement.dataset.presentation = String(enabled);
    window.localStorage.setItem(PRESENTATION_KEY, String(enabled));
  }, [enabled]);

  const toggle = useCallback(() => setEnabled(current => !current), []);
  return [enabled, toggle];
}

export function formatDuration(ms?: number): string {
  if (ms === undefined || Number.isNaN(ms)) return '—';
  if (ms < 1) return '<1 ms';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

export function formatMoney(amount: number, currency = 'RON'): string {
  return `${new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)} ${currency}`;
}

export function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString('en-GB', { hour12: false });
}
